import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { uploadImageWithCompression } from '../utils/firebaseStorage';

import Section1BasicInfo       from '../components/matrimony-form/Section1BasicInfo';
import Section2CommunityInfo   from '../components/matrimony-form/Section2CommunityInfo';
import Section3ProfessionalInfo from '../components/matrimony-form/Section3ProfessionalInfo';
import Section4Lifestyle       from '../components/matrimony-form/Section4Lifestyle';
import Section5FamilyDetails   from '../components/matrimony-form/Section5FamilyDetails';
import Section6Astro           from '../components/matrimony-form/Section6Astro';
import Section7PartnerPrefs    from '../components/matrimony-form/Section7PartnerPrefs';
import Section8Contact         from '../components/matrimony-form/Section8Contact';

const STEPS = [
  { id: 1, label: 'Basic Info',    icon: '👤', component: Section1BasicInfo },
  { id: 2, label: 'Community',     icon: '🏺', component: Section2CommunityInfo },
  { id: 3, label: 'Professional',  icon: '🎓', component: Section3ProfessionalInfo },
  { id: 4, label: 'Lifestyle',     icon: '🌿', component: Section4Lifestyle },
  { id: 5, label: 'Family',        icon: '👨‍👩‍👧', component: Section5FamilyDetails },
  { id: 6, label: 'Astrology',     icon: '🔯', component: Section6Astro },
  { id: 7, label: 'Partner',       icon: '💑', component: Section7PartnerPrefs },
  { id: 8, label: 'Contact',       icon: '📞', component: Section8Contact },
];

const INITIAL_FORM = {
  fullName: '', gender: '', dob: '', age: '', height: '', weight: '',
  complexion: '', bloodGroup: '', maritalStatus: 'Never Married',
  motherTongue: '', disabilities: 'None',
  religion: 'Hindu', caste: 'Kumbhar', subCaste: '', nativePlace: '',
  nativeDistrict: '', nativeTaluka: '', nativeState: 'Maharashtra',
  education: '', educationDetails: '', occupation: '', employedIn: '',
  company: '', workLocation: '', annualIncome: '',
  diet: '', smoking: 'No', drinking: 'No', hobbies: '', languages: '',
  fatherName: '', fatherOccupation: '', fatherStatus: 'Alive',
  motherName: '', motherOccupation: '', motherStatus: 'Alive',
  brothers: '0', brothersMarried: '0', sisters: '0', sistersMarried: '0',
  familyType: '', familyStatus: '', familyValues: '', familyLocation: '',
  gotra: '', manglik: '', rashiBirth: '', nakshatra: '',
  birthTime: '', birthPlace: '', hasKundali: '',
  partnerAgeMin: '', partnerAgeMax: '', partnerHeightMin: '', partnerHeightMax: '',
  partnerMaritalStatus: '', partnerReligion: 'Hindu', partnerCaste: 'Kumbhar',
  partnerEducation: '', partnerOccupation: '', partnerLocation: '',
  partnerIncome: '', partnerManglik: '', partnerExpectations: '',
  contactNumber: '', alternateNumber: '', email: '',
  currentResidence: '', permanentAddress: '', profilePhotoUrl: '',
};

const CreateProfilePage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { success, error, info, warning } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData]       = useState(INITIAL_FORM);
  const [imageFile, setImageFile]     = useState(null);
  const [loading, setLoading]         = useState(false);
  const [hasProfile, setHasProfile]   = useState(false);
  const [status, setStatus]           = useState('');

  // ── 1. Redirect if not logged in ──────────────────────────────
  useEffect(() => {
    if (currentUser === null) {
      // null means auth resolved and nobody is logged in
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // ── 2. Pre-fill if user already has a profile ──────────────────
  useEffect(() => {
    if (!currentUser) return;

    // Always pre-fill email
    setFormData(prev => ({ ...prev, email: currentUser.email || '' }));

    // Try to load existing profile (best effort — never block the form)
    const load = async () => {
      try {
        const { data } = await api.get('/matrimony/profile');
        if (data && data.fullName) {
          setFormData(prev => ({ ...prev, ...data }));
          setHasProfile(true);
          info('Editing your existing profile');
        }
      } catch (err) {
        // 404 = no profile yet. Anything else = ignore, still show form
      }
    };
    load();
  }, [currentUser, info]);

  // ── Handlers ──────────────────────────────────────────────────
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleFileChange = useCallback((file) => {
    setImageFile(file);
  }, []);

  const handleNext = () => {
    setCurrentStep(s => Math.min(s + 1, STEPS.length));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrev = () => {
    setCurrentStep(s => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStepClick = (id) => {
    if (id < currentStep) {
      setCurrentStep(id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ── Submit ────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName?.trim()) {
      error('Please enter your full name (Section 1)');
      setCurrentStep(1);
      return;
    }
    if (!formData.gender) {
      error('Please select your gender (Section 1)');
      setCurrentStep(1);
      return;
    }
    if (!formData.contactNumber?.trim()) {
      error('Please enter your contact number (Section 8)');
      setCurrentStep(8);
      return;
    }

    setLoading(true);

    try {
      let photoUrl = formData.profilePhotoUrl || '';

      if (imageFile) {
        setStatus('Uploading photo...');
        try {
          photoUrl = await Promise.race([
            uploadImageWithCompression(
              imageFile,
              `matrimony-profiles/${currentUser.uid}`,
              { maxWidth: 800, maxHeight: 800, quality: 0.85 }
            ),
            new Promise((_, rej) =>
              setTimeout(() => rej(new Error('Upload timeout')), 30000)
            ),
          ]);
        } catch (uploadErr) {
          warning('Photo could not be uploaded — saving profile without it.');
          photoUrl = '';
        }
      }

      setStatus('Saving profile...');

      // Remove undefined / null values (Firestore doesn't accept them)
      const clean = {};
      Object.entries(formData).forEach(([k, v]) => {
        if (v !== undefined && v !== null) clean[k] = v;
      });

      const payload = {
        ...clean,
        profilePhotoUrl: photoUrl,
        userId:          currentUser.uid,
        userEmail:       currentUser.email || '',
        updatedAt:       new Date().toISOString(),
        ...(!hasProfile && { createdAt: new Date().toISOString() }),
      };

      if (hasProfile) {
        await api.put('/matrimony/profile', payload);
        success('🏺 Profile updated! Jai Kumbhar Samaj!');
      } else {
        await api.post('/matrimony/profile', payload);
        success('🎉 Profile created! Welcome to Kumbhar Samaj Matrimony!');
      }

      navigate('/matrimony');
    } catch (err) {
      console.error('Profile save error:', err);
      const status = err.status || err.response?.status;
      if (status === 401) {
        error('Session expired. Please log in again.');
        navigate('/login');
      } else {
        error(err.message || 'Failed to save profile. Please try again.');
      }
    } finally {
      setLoading(false);
      setStatus('');
    }
  };

  // ── Guard: show nothing while Firebase resolves auth ──────────
  // currentUser is `undefined` while auth is loading, `null` after resolved (no user)
  if (currentUser === undefined) {
    return (
      <div className="cp-spin">
        <div className="cp-spin__pot">🏺</div>
        <p>Loading...</p>
        <style jsx>{`
          .cp-spin {
            min-height: 70vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 16px;
            color: var(--neutral-600);
          }
          .cp-spin__pot {
            font-size: 3rem;
            animation: float 2s ease-in-out infinite;
          }
          @keyframes float {
            0%,100% { transform: translateY(0); }
            50%      { transform: translateY(-10px); }
          }
        `}</style>
      </div>
    );
  }

  // currentUser is null — redirect handled by useEffect above
  if (!currentUser) return null;

  const CurrentSection    = STEPS[currentStep - 1].component;
  const progressPercent   = Math.round((currentStep / STEPS.length) * 100);

  return (
    <div className="cp-page">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="cp-header">
        <div className="container">
          <div className="cp-header__inner">
            <div>
              <h1>{hasProfile ? '✏️ Edit Matrimony Profile' : '🏺 Create Matrimony Profile'}</h1>
              <p>{hasProfile
                ? 'Update your profile to find the perfect match'
                : 'Fill in all 8 sections to complete your profile'}
              </p>
            </div>
            <div className="cp-verified-badge">🏺 Kumbhar Samaj Verified</div>
          </div>
        </div>
      </div>

      {/* ── Layout ─────────────────────────────────────────────── */}
      <div className="container">
        <div className="cp-layout">

          {/* Sidebar */}
          <aside className="cp-sidebar">
            <div className="cp-sidebar__card">
              <p className="cp-sidebar__label">📋 PROFILE SECTIONS</p>

              {STEPS.map(step => {
                const done   = step.id < currentStep;
                const active = step.id === currentStep;
                const locked = step.id > currentStep;
                return (
                  <button
                    key={step.id}
                    type="button"
                    disabled={locked}
                    onClick={() => handleStepClick(step.id)}
                    className={`cp-step ${done ? 'done' : ''} ${active ? 'active' : ''} ${locked ? 'locked' : ''}`}
                  >
                    <div className="cp-step__icon">
                      {done
                        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                        : step.icon}
                    </div>
                    <div className="cp-step__text">
                      <span className="cp-step__name">{step.label}</span>
                    </div>
                    {active && <div className="cp-step__dot" />}
                  </button>
                );
              })}

              <div className="cp-progress">
                <div className="cp-progress__label">
                  <span>Progress</span>
                  <strong>{progressPercent}%</strong>
                </div>
                <div className="cp-progress__bar">
                  <div className="cp-progress__fill" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
            </div>
          </aside>

          {/* Main form */}
          <main className="cp-main">
            <form onSubmit={handleSubmit} noValidate>

              {/* Section card */}
              <div className="cp-card">
                <div className="cp-card__header">
                  <span className="cp-card__emoji">{STEPS[currentStep - 1].icon}</span>
                  <div>
                    <h2>{STEPS[currentStep - 1].label}</h2>
                    <p>Step {currentStep} of {STEPS.length}</p>
                  </div>
                  <div className="cp-card__badge">{currentStep}/{STEPS.length}</div>
                </div>
                <div className="cp-card__body">
                  <CurrentSection
                    formData={formData}
                    handleChange={handleChange}
                    handleFileChange={handleFileChange}
                  />
                </div>
              </div>

              {/* Status */}
              {status && (
                <div className="cp-status">
                  <div className="cp-status__spinner" />
                  <span>{status}</span>
                </div>
              )}

              {/* Navigation */}
              <div className="cp-nav">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handlePrev}
                  disabled={currentStep === 1 || loading}
                >
                  ← Back
                </button>

                <div className="cp-dots">
                  {STEPS.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      className={`cp-dot ${s.id === currentStep ? 'active' : ''} ${s.id < currentStep ? 'done' : ''}`}
                      onClick={() => handleStepClick(s.id)}
                      disabled={s.id > currentStep}
                      title={s.label}
                    />
                  ))}
                </div>

                {currentStep < STEPS.length ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleNext}
                    disabled={loading}
                  >
                    Next →
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={loading}
                  >
                    {loading
                      ? <><span className="btn-spinner" /> {status || 'Saving...'}</>
                      : hasProfile ? '✅ Update Profile' : '🎉 Submit Profile'}
                  </button>
                )}
              </div>

            </form>
          </main>
        </div>
      </div>

      <style jsx>{`
        .cp-page {
          min-height: 100vh;
          background: var(--neutral-50);
          padding-bottom: var(--space-16);
        }

        /* Header */
        .cp-header {
          background: linear-gradient(135deg, var(--primary-800), var(--primary-600));
          padding: var(--space-12) 0;
        }
        .cp-header__inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--space-6);
        }
        .cp-header h1 {
          font-size: var(--text-4xl);
          color: white;
          margin-bottom: var(--space-2);
        }
        .cp-header p {
          color: rgba(255,255,255,0.85);
          margin: 0;
        }
        .cp-verified-badge {
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.3);
          color: white;
          padding: var(--space-2) var(--space-5);
          border-radius: var(--radius-full);
          font-size: var(--text-sm);
          font-weight: var(--weight-semibold);
          white-space: nowrap;
          flex-shrink: 0;
        }

        /* Layout */
        .cp-layout {
          display: grid;
          grid-template-columns: 260px 1fr;
          gap: var(--space-6);
          margin-top: var(--space-8);
        }

        /* Sidebar */
        .cp-sidebar { position: sticky; top: 90px; height: fit-content; }
        .cp-sidebar__card {
          background: white;
          border-radius: var(--radius-xl);
          padding: var(--space-5);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--neutral-200);
        }
        .cp-sidebar__label {
          font-size: 11px;
          font-weight: var(--weight-bold);
          color: var(--primary-700);
          letter-spacing: 0.8px;
          margin-bottom: var(--space-4);
          padding-bottom: var(--space-3);
          border-bottom: 2px solid var(--primary-100);
        }

        .cp-step {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          width: 100%;
          padding: var(--space-3);
          background: none;
          border: 1px solid transparent;
          border-radius: var(--radius-lg);
          cursor: default;
          transition: all var(--transition-fast);
          text-align: left;
          position: relative;
          font-family: var(--font-body);
          margin-bottom: 2px;
        }
        .cp-step.done   { cursor: pointer; }
        .cp-step.done:hover { background: var(--success-light); }
        .cp-step.active { background: var(--primary-50); border-color: var(--primary-200); }
        .cp-step.locked { opacity: 0.4; }

        .cp-step__icon {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: var(--neutral-100);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          flex-shrink: 0;
          transition: all var(--transition-base);
        }
        .cp-step.active .cp-step__icon {
          background: var(--primary-500);
          color: white;
          box-shadow: 0 0 0 3px var(--primary-100);
        }
        .cp-step.done .cp-step__icon {
          background: var(--success);
          color: white;
        }

        .cp-step__name {
          font-size: var(--text-sm);
          font-weight: var(--weight-semibold);
          color: var(--neutral-800);
        }
        .cp-step.active .cp-step__name { color: var(--primary-700); }
        .cp-step.done   .cp-step__name { color: var(--success); }

        .cp-step__dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--primary-500);
          margin-left: auto;
          flex-shrink: 0;
        }

        /* Progress */
        .cp-progress {
          margin-top: var(--space-5);
          padding-top: var(--space-4);
          border-top: 1px solid var(--neutral-200);
        }
        .cp-progress__label {
          display: flex;
          justify-content: space-between;
          font-size: var(--text-xs);
          color: var(--neutral-600);
          margin-bottom: var(--space-2);
        }
        .cp-progress__label strong { color: var(--primary-600); }
        .cp-progress__bar {
          height: 6px;
          background: var(--neutral-200);
          border-radius: var(--radius-full);
          overflow: hidden;
        }
        .cp-progress__fill {
          height: 100%;
          background: linear-gradient(90deg, var(--primary-500), var(--gold-500));
          border-radius: var(--radius-full);
          transition: width var(--transition-slow);
        }

        /* Section card */
        .cp-card {
          background: white;
          border-radius: var(--radius-2xl);
          box-shadow: var(--shadow-md);
          border: 1px solid var(--neutral-200);
          overflow: hidden;
          margin-bottom: var(--space-4);
          animation: sectionIn 0.3s ease-out;
        }
        @keyframes sectionIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .cp-card__header {
          display: flex;
          align-items: center;
          gap: var(--space-4);
          padding: var(--space-6) var(--space-8);
          background: linear-gradient(135deg, var(--primary-50), var(--gold-100));
          border-bottom: 2px solid var(--primary-100);
        }
        .cp-card__emoji { font-size: 2.2rem; line-height: 1; }
        .cp-card__header h2 {
          font-size: var(--text-2xl);
          color: var(--primary-800);
          margin: 0;
        }
        .cp-card__header p {
          font-size: var(--text-sm);
          color: var(--neutral-500);
          margin: 2px 0 0;
        }
        .cp-card__badge {
          margin-left: auto;
          font-size: var(--text-sm);
          font-weight: var(--weight-bold);
          color: var(--primary-500);
          background: var(--primary-100);
          padding: var(--space-1) var(--space-3);
          border-radius: var(--radius-full);
          flex-shrink: 0;
        }
        .cp-card__body { padding: var(--space-8); }

        /* Status */
        .cp-status {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          background: var(--primary-50);
          border: 1px solid var(--primary-200);
          border-radius: var(--radius-lg);
          padding: var(--space-3) var(--space-5);
          margin-bottom: var(--space-4);
          font-size: var(--text-sm);
          color: var(--primary-700);
          font-weight: var(--weight-medium);
        }
        .cp-status__spinner {
          width: 18px;
          height: 18px;
          border: 2px solid var(--primary-200);
          border-top-color: var(--primary-500);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          flex-shrink: 0;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Navigation */
        .cp-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--space-4);
          background: white;
          border-radius: var(--radius-xl);
          padding: var(--space-4) var(--space-6);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--neutral-200);
        }
        .cp-dots { display: flex; gap: var(--space-2); align-items: center; }
        .cp-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: var(--neutral-300);
          border: none;
          cursor: default;
          padding: 0;
          transition: all var(--transition-base);
        }
        .cp-dot.active { background: var(--primary-500); transform: scale(1.4); }
        .cp-dot.done   { background: var(--success); cursor: pointer; }

        /* Submit button */
        .btn-submit {
          display: inline-flex;
          align-items: center;
          gap: var(--space-2);
          background: linear-gradient(135deg, var(--gold-700), var(--gold-500));
          color: var(--earth-900, #3E2723);
          border: none;
          font-weight: var(--weight-bold);
          padding: var(--space-3) var(--space-6);
          border-radius: var(--radius-lg);
          cursor: pointer;
          font-family: var(--font-body);
          font-size: var(--text-base);
          transition: all var(--transition-base);
        }
        .btn-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(212,175,55,0.35);
        }
        .btn-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        .btn-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(0,0,0,0.2);
          border-top-color: #3E2723;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        /* Responsive */
        @media (max-width: 900px) {
          .cp-layout { grid-template-columns: 1fr; }
          .cp-sidebar { position: static; }
          .cp-sidebar__card {
            display: flex;
            flex-wrap: wrap;
            gap: var(--space-2);
            padding: var(--space-3);
          }
          .cp-sidebar__label { width: 100%; margin-bottom: 0; }
          .cp-step {
            flex: 1;
            min-width: 80px;
            flex-direction: column;
            align-items: center;
            padding: var(--space-2);
            text-align: center;
          }
          .cp-step__dot { display: none; }
          .cp-progress { width: 100%; margin-top: var(--space-3); }
        }
        @media (max-width: 600px) {
          .cp-card__body { padding: var(--space-4); }
          .cp-card__header { padding: var(--space-4); }
          .cp-header h1 { font-size: var(--text-2xl); }
          .cp-verified-badge { display: none; }
          .cp-step__name { font-size: 10px; }
        }
      `}</style>
    </div>
  );
};

export default CreateProfilePage;